import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import CreateProject from './pages/CreateProject';
import CharacterLevel from './pages/CharacterLevel';
import CharacterCanvas from './pages/CharacterCanvas';
import { Navigate as RouterNavigate, useParams as useRouteParams } from 'react-router-dom';
import { SCROLL_CANVAS } from './lib/featureFlags';

// Canvas-canonical character route. With the flag off, fall back to the
// wizard's level 0 so bookmarks of /character/:id keep working either way.
function CharacterCanvasRoute() {
  const { projectId } = useRouteParams();
  if (!SCROLL_CANVAS) return <RouterNavigate to={`/character/${projectId}/level/0`} replace />;
  return <CharacterCanvas />;
}
import CharacterWeb from './pages/CharacterWeb';
import PlotSetup from './pages/PlotSetup';
import PlotLevel from './pages/PlotLevel';
import PaymentCallback from './pages/PaymentCallback';
import UpgradeToPro from './pages/UpgradeToPro';
import WorldBuilding from './pages/WorldBuilding';
import SceneBuilder from './pages/SceneBuilder';
import ProjectWorkspace from './pages/ProjectWorkspace';
import NewProject from './pages/NewProject';
import SeriesBuilder from './pages/SeriesBuilder';
import Settings from './pages/Settings';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import { ProjectProvider } from './context/ProjectContext';
import { Analytics } from '@vercel/analytics/react';
import './i18n';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Remount the workspace when the project id changes. Without a `key`, React
// reuses the SAME component instance across /project/A → /project/B (same
// route, only the param changes), carrying that instance's per-project refs
// — the screenplay session draft AND its rev (docRef.current / docRef.rev) —
// into the next project. Keying on projectId forces a clean unmount+remount
// so a different project always starts from a fresh slate. (The rev half
// already failed safe via a 409, but the draft half could seed project B's
// editor with project A's unsaved text.)
function KeyedProjectWorkspace() {
  const { projectId } = useParams();
  return <ProjectWorkspace key={projectId} />;
}

function KeyedSeriesBuilder() {
  const { projectId } = useParams();
  return <SeriesBuilder key={projectId} />;
}

function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment (not query params) for session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        {/* Same card, opened in sign-up mode. A real route (not a query flag)
            so it is linkable, bookmarkable, and shows up as its own pageview
            in the funnel. /login?mode=signup is honoured too, for any link
            already in the wild. */}
        <Route path="/signup" element={<Login initialMode="signup" />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-project"
          element={
            <ProtectedRoute>
              <CreateProject />
            </ProtectedRoute>
          }
        />
        <Route
          path="/character/:projectId/level/:levelNum"
          element={
            <ProtectedRoute>
              <CharacterLevel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/character/:projectId"
          element={
            <ProtectedRoute>
              <CharacterCanvasRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/character-web"
          element={
            <ProtectedRoute>
              <CharacterWeb />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plot/:projectId/setup"
          element={
            <ProtectedRoute>
              <PlotSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plot/:projectId/level/:levelNum"
          element={
            <ProtectedRoute>
              <PlotLevel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/upgrade"
          element={
            <ProtectedRoute>
              <UpgradeToPro />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/callback"
          element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/world-building"
          element={
            <ProtectedRoute>
              <WorldBuilding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scenes"
          element={
            <ProtectedRoute>
              <SceneBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId"
          element={
            <ProtectedRoute>
              <KeyedProjectWorkspace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-project"
          element={
            <ProtectedRoute>
              <NewProject />
            </ProtectedRoute>
          }
        />
        <Route
          path="/series/:projectId"
          element={
            <ProtectedRoute>
              <KeyedSeriesBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Unknown URLs (incl. the removed /payment-success) previously
            rendered a blank screen — send them somewhere useful instead. */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ProjectProvider>
          <AppRouter />
        </ProjectProvider>
      </BrowserRouter>
      {/* Vercel Web Analytics — pageviews only, no cookies, no PII. Inert
          outside Vercel, so local dev and the Render backend are unaffected. */}
      <Analytics />
    </div>
  );
}

export default App;
